import { useId, useState } from 'react'
import { ApiClientError, type Session } from '../api/types.js'
import { Alert } from '../ui/Alert.js'

/**
 * Узкий срез клиента: экрану входа больше ничего не нужно, а слушать на нём
 * весь `AdminClient` заставило бы каждый тест собирать полсотни полей,
 * которых он не проверяет (барьер 2 — двойник клиента API разрешён здесь).
 */
export interface LoginClient {
  login(email: string, password: string): Promise<Session>
}

export interface LoginProps {
  client: LoginClient
  onDone: (session: Session) => void
  /**
   * Почта, уже известная порталу: сразу после первичной настройки её
   * незачем набирать второй раз — человек ввёл её минуту назад
   * (FR-INST-014).
   */
  email?: string
}

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось войти. Проверьте соединение и попробуйте ещё раз.'

export function Login(props: LoginProps) {
  const { client, onDone } = props
  const emailId = useId()
  const passwordId = useId()

  const [email, setEmail] = useState(props.email ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Единственная защита от второй попытки входа: сервер считает повторное
  // нажатие отдельной неудачей и вдвое быстрее упирает человека
  // в ограничение частоты (FR-ADM-006).
  const [pending, setPending] = useState(false)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return

    setPending(true)
    setError(null)
    try {
      const session = await client.login(email, password)
      onDone(session)
    } catch (cause) {
      // Причина отказа показывается как пришла от сервера — он пишет
      // по-русски и объясняет её лучше, чем общее «произошла ошибка».
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="вход">
      <main className="shell">
        {/* Три метки QR — знак продукта. Ровно с этого начинается работа
            с платформой: номер подключают, сканируя код телефоном.
            Третья горит цветом связи — тем же, каким весь портал
            показывает, что соединение живое. */}
        <div className="вход__метки" aria-hidden="true">
          <span className="вход__метка" />
          <span className="вход__метка" />
          <span className="вход__метка" />
        </div>
        <h1>RK WhatsApp</h1>
        <p className="shell__hint">
          Номера на связи и сценарии, которые отвечают вместо человека.
        </p>
        <div className="card">
        <form onSubmit={(event) => void handleSubmit(event)}>
          <div className="field">
            <label htmlFor={emailId}>Адрес электронной почты</label>
            <input
              id={emailId}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor={passwordId}>Пароль</label>
            <input
              id={passwordId}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}
          <button type="submit" className="btn btn--primary" disabled={pending}>
            Войти
          </button>
        </form>
        </div>
      </main>
    </div>
  )
}
